import React, { useEffect, useMemo, useRef } from 'react';
import { useAppSelector } from 'hooks';

import { Row, TableView, TableViewHandle } from '~components/table-view';

import { COPY_STATUS, Section, SectionItem } from '~support-at-home/models';

import { columnsFactory, minTableWidth } from '~support-at-home/forms/budgets/sectionItemsList';

import { selectLookupCalendarFrequencies } from '~lookups/selectors/calendarFrequencies';
import { selectLookupShifts } from '~lookups/selectors/shifts';

import SectionTotal from './BudgetSectionTotal';

interface ComponentProps {
  id: string;
  section: Section;
  initialValues?: Section;
  start_date?: string;
  end_date?: string;
  /** Rows whose figures are awaiting a recalculation, so their price and total show a spinner. */
  pendingItemIds?: Set<string>;
  filteredServices: any[];
  readonly: boolean;
  /** Upstream callbacks for WizardForm-managed data */
  onUpdate: (sectionId: string, items: SectionItem[]) => void;
  onDelete?: (sectionId: string, item: SectionItem) => void;
}

const BudgetSection: React.FC<ComponentProps> = React.memo(
  ({
    id,
    section,
    initialValues,
    start_date = '',
    end_date = '',
    filteredServices,
    readonly = false,
    pendingItemIds,
    onUpdate,
    onDelete,
  }) => {
    const calendarFrequencies = useAppSelector(selectLookupCalendarFrequencies);
    const shifts = useAppSelector(selectLookupShifts);

    const { category_id, category, items, contrib_proportion } = section;

    // A saved budget's items come back without a category - the column lives on the section, not the
    // item. Stamping it on before the table sees them means every row carries the pair that
    // identifies which Support at Home service period it books against, now that the same service
    // type can sit on two of them. handleChange already writes the same value back on edit; this
    // makes it true on load as well.
    const rows = useMemo(
      () => (items ?? []).map((item) => ({ ...item, category_id: item.category_id ?? category_id })),
      [items, category_id],
    );

    // Item ids are what the table rows are keyed by; the totals panel is keyed by service type.
    const pendingServiceTypeIds = useMemo(
      () =>
        new Set(
          (items ?? [])
            .filter((item) => pendingItemIds?.has(item.id))
            .map((item) => String(item.service_type_id)),
        ),
      [items, pendingItemIds],
    );

    const tableRef = useRef<TableViewHandle>(null);
    const columns = useMemo(() => {
      return columnsFactory(shifts, calendarFrequencies, filteredServices, initialValues);
    }, [shifts, calendarFrequencies, filteredServices, initialValues]);

    // Paint the invalid cells as soon as a line falls outside the period its service is available
    // for, rather than only after a rejected save. The check is a local Yup pass over the rows in
    // one section, so it is cheap enough to run on change.
    useEffect(() => {
      tableRef.current?.validate();
    }, [rows, filteredServices, start_date, end_date]);

    const handleDelete = (rowId: string | number) => {
      const row = items.find(({ id }) => id === rowId);
      if (row) {
        onDelete?.(category_id, row);
      }
    };

    const handleChange = (allRows: Row[]) => {
      const updatedRows = allRows.map((row) => ({
        ...row,
        category_id: row.category_id ?? category_id,
        start_date: row.start_date ?? start_date,
        end_date: row.end_date ?? end_date,
      })) as SectionItem[];

      onUpdate?.(category_id, updatedRows);
    };

    // Only check for changes if we have initial values, if not "its a new budget"
    const contributionChanged = initialValues && initialValues?.contrib_proportion !== contrib_proportion;

    return (
      <div key={id}>
        <div className="budget-section-container">
          <div className="section-header">
            <div>
              <strong>Category:</strong> {category?.name}
            </div>
            <div className={`p-2 rounded ${contributionChanged ? 'bg-warning' : ''}`}>
              <strong>Co-Contribution:</strong> {contrib_proportion}%
            </div>
          </div>
          <TableView
            ref={tableRef}
            id={category_id}
            data={rows}
            columns={columns}
            context={{
              category_id,
              start_date,
              end_date,
              initialRows: initialValues?.items || [],
              pendingItemIds,
            }}
            onChange={handleChange}
            onDeleteRow={handleDelete}
            isRowDisabled={(row) => row.copy_status === COPY_STATUS.REMOVED}
            canDeleteRow={(row) => row.copy_status !== COPY_STATUS.REMOVED}
            emptyMessage="No items found"
            readonly={readonly}
            editonly
            compactView
            minimalView
            minTableWidth={minTableWidth}
            bordered
          />
        </div>
        <div className="section-totals-container">
          <SectionTotal
            category_id={category_id}
            section={section}
            // The panel breaks its figures down per service, so it needs to know which SERVICES are
            // waiting rather than merely that something is. A row's own service is the only line in
            // there its change can move; the section summary below them waits on all of them.
            pendingServiceTypeIds={pendingServiceTypeIds}
          />
        </div>
      </div>
    );
  },
);

BudgetSection.displayName = 'BudgetSection';
export default BudgetSection;
